import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    SelectAll as SelectAllIcon,
    Deselect as DeselectIcon,
    RestartAlt as ResetIcon,
} from '@mui/icons-material';

import {
    MODE_CATALOGS,
    MODE_TYPES,
    loadModeFilter,
    saveModeFilter,
    CHANGE_EVENT,
} from './mode_catalog';

// Returns a fresh filter where every mode for every vehicle type is enabled.
const buildAllEnabled = () => {
    const next = {};
    for (const type of MODE_TYPES) {
        next[type] = {};
        for (const id of Object.keys(MODE_CATALOGS[type])) {
            next[type][id] = true;
        }
    }
    return next;
};

// Make sure every vehicle type has an entry so the checkbox UI starts from a
// known state even when the user has never opened this page before.
const hydrate = (raw) => {
    const base = buildAllEnabled();
    if (!raw || typeof raw !== 'object') return base;
    for (const type of MODE_TYPES) {
        if (raw[type] && typeof raw[type] === 'object') {
            const merged = {};
            for (const id of Object.keys(MODE_CATALOGS[type])) {
                merged[id] = Object.prototype.hasOwnProperty.call(raw[type], id)
                    ? !!raw[type][id]
                    : true;
            }
            base[type] = merged;
        }
    }
    return base;
};

const ModeListSettings = () => {
    const [filter, setFilter] = useState(() => hydrate(loadModeFilter()));

    // Reflect external changes (e.g. from another tab/window) into the UI.
    useEffect(() => {
        const onChange = (e) => {
            const next = (e && e.detail && e.detail.filter) || loadModeFilter();
            setFilter(hydrate(next));
        };
        window.addEventListener(CHANGE_EVENT, onChange);
        return () => window.removeEventListener(CHANGE_EVENT, onChange);
    }, []);

    const persist = (next) => {
        setFilter(next);
        saveModeFilter(next);
    };

    const toggle = (type, id) => {
        const next = { ...filter, [type]: { ...filter[type], [id]: !filter[type][id] } };
        persist(next);
    };

    const setAllForType = (type, value) => {
        const ids = Object.keys(MODE_CATALOGS[type]);
        const map = {};
        for (const id of ids) map[id] = value;
        persist({ ...filter, [type]: map });
    };

    const resetAll = () => persist(buildAllEnabled());

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Mode List Filter
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
                    Choose which modes appear in the Connection panel's Mode selector for each vehicle type.
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ResetIcon />}
                    onClick={resetAll}
                >
                    Enable All
                </Button>
            </Stack>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 2,
                }}
            >
                {MODE_TYPES.map((type) => {
                    const catalog = MODE_CATALOGS[type];
                    const ids = Object.keys(catalog);
                    const enabledCount = ids.filter((id) => filter[type] && filter[type][id]).length;
                    return (
                        <Paper key={type} variant="outlined" sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{type}</Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`${enabledCount} / ${ids.length}`}
                                    sx={{ height: 20, fontSize: 11 }}
                                />
                                <Box sx={{ flex: 1 }} />
                                <Button
                                    size="small"
                                    startIcon={<SelectAllIcon fontSize="inherit" />}
                                    onClick={() => setAllForType(type, true)}
                                    sx={{ minWidth: 0, fontSize: 11 }}
                                >
                                    All
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<DeselectIcon fontSize="inherit" />}
                                    onClick={() => setAllForType(type, false)}
                                    sx={{ minWidth: 0, fontSize: 11 }}
                                    color="inherit"
                                >
                                    None
                                </Button>
                            </Stack>
                            <Divider />
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    columnGap: 0.5,
                                }}
                            >
                                {ids.map((id) => (
                                    <FormControlLabel
                                        key={id}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={!!(filter[type] && filter[type][id])}
                                                onChange={() => toggle(type, id)}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ fontSize: 12 }}>
                                                <Box component="span" sx={{ color: 'text.disabled', mr: 0.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                                                    {id.padStart(2, ' ')}
                                                </Box>
                                                {catalog[id]}
                                            </Typography>
                                        }
                                        sx={{ m: 0, py: 0 }}
                                    />
                                ))}
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};

export default ModeListSettings;
